import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { storage } from '@abyss/web/tools/storage';
import find from 'lodash/find';
import pick from 'lodash/pick';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { adobeImpressionTrackEvent } from '../../../../common/AdobeTagging/adobeImpressionTrackEvent';
import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../../common/AdobeTagging/adobeModalTrackEvent';
import { adobeSearchModifiedEvent } from '../../../../common/AdobeTagging/adobeSearchModifiedEvent';
import { adobeStandardSearchEvent } from '../../../../common/AdobeTagging/adobeStandardSearchEvent';
import {
  CareCategories,
  Constants,
  LOB,
  ReverseCoverageTypesCodes,
  SectionType,
  errorCodeRouteMap,
} from '../../../../common/Constants';
import {
  FeatureFlags,
  SECTION_TERM,
} from '../../../../common/ConstantsFeatureFlags';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { ConstantsRoutes } from '../../../../common/ConstantsRoutes';
import { phoneOnly } from '../../../../common/ConstantsStyles';
import { DisclaimersContainer } from '../../../../common/DisclaimersContainer/DisclaimersContainer';
import { SectionSkeleton } from '../../../../common/LazyLoaderSkeleton/SectionSkeleton';
import { CrisisInterventionModal } from '../../../../common/modal/CrisisInterventionModal';
import { NullResultsPage } from '../../../../common/NullResultsPage';
import { CustomNullSearchPage } from '../../../../common/NullResultsPage/CustomNullSearchPage';
import { PSXHeader } from '../../../../common/PSXHeader';
import {
  CoverageType,
  MemberInfo,
} from '../../../../common/PSXHeader/ObapiDemo/memberProfile';
import {
  getMpCodes,
  getProgramPromotionsForTypeahead,
  isMNRHbpbCodePresent,
} from '../../../../common/PSXHeader/SearchBar/utils';
import { StillNeedHelp } from '../../../../common/StillNeedHelp';
import {
  convertObjectToUrlParams,
  getFeatureFlag,
} from '../../../../common/Utils';
import {
  convertCoverageType,
  getIndicatorsForImpressions,
  getSearchLocation,
  getTier1Indicator,
} from '../../../../common/Utils/adobeTrackUtils/adobeTrackUtils';
import { CountySearchContext } from '../../../../context/CountySearchContext';
import { SearchFilterContext } from '../../../../context/SearchFilterContext';
import { useAdobePageTrackEvent } from '../../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useCoverageType } from '../../../../hooks/useCoverageType';
import { useCurrentMemberOrDependentInfo } from '../../../../hooks/useCurrentMember';
import { useCurrentMemberIsPreEffective } from '../../../../hooks/useCurrentMemberIsPreEffective';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useGeoLocationStorage } from '../../../../hooks/useGeoLocationStorage';
import { useInitializeStore } from '../../../../hooks/useInitializeStore';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useSmartChoice } from '../../../../hooks/useSmartChoice';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { useTypeAheadData } from '../../../../hooks/useTypeAheadData';
import { resolvedCombinedSearchResults } from '../../../../hooks/useTypeAheadData/utils';
import { BreadcrumbSessionStorage } from '../../../../models/BreadcrumbSessionStorage';
import { useAnalyticsStore } from '../../../../store/useAnalyticsStore';
import { useCeOffersStore } from '../../../../store/useCeOffersStore';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { useErrorStore } from '../../../../store/useErrorStore';
import { usePCPStore } from '../../../../store/usePCPStore';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import { useVirtualCareStore } from '../../../../store/useVirtualCareStore';
import { returnSuppressFlag } from '../../../../utils/featureSuppress';
import { getSuppressFacilityFlags } from '../../../../utils/providerDetails.utils';
import {
  getCategoryVirtualCareProvidersFromEligibilities,
  getLagoonConfigValue,
} from '../../../../utils/providerSearch.utils';
import {
  getLoggedInMember,
  getMemberHasDesignatedNetwork,
  isOHCNSMember,
} from '../../../../utils/user.utils';
import { getPlanSelectorVariables } from '../../context/PlanSelector/helpers';
import { VirtualCareList } from '../MapView/VirtualCareList';
import { NullPageContainerStyled } from '../NullSpecialtyPage/NullSpecialtyPage.styles';
import { SearchedResultSectionData } from './SearchedResultSectionData';
import {
  getCorrectChipCategory,
  getSearchType,
  getSelectedCoverageType,
} from './SearchResults.UtilsV2';
import {
  getNoOfResultsForAnalytics,
  getProviderType,
  getProviderTypesFromDisplayedResults,
} from './searchUtils';
import {
  combineProvidersAcrossSections,
  compareAndReturn,
  getAllProvidersData,
  getFirstEnabledChip,
  useCheckForPseudoRollupCodes,
} from './utils';

export const SearchResultsV2 = () => {
  // TODO: Check if this required.
  // initialize store values from url searchParams for search results page
  const { providerType = '' } = useInitializeStore(
    ConstantsRoutes.PROVIDER_SEARCH_RESULTS.key
  );
  const { ACTIVE_MEMBER_INDEX } = Constants.STORAGE_KEYS.SESSION;
  const activeMemberIndex =
    Number(storage.session.get(ACTIVE_MEMBER_INDEX)) ?? 0;
  const storedMembers = useStore(StoreKeys.OBAPI_MEMBERS);
  const [memberProfile] = useState<MemberInfo[]>(storedMembers);
  const [virtualCareCardShow, setVirtualCareCardShow] =
    useState<boolean>(false);
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const storedLocation = useGeoLocationStorage();
  const mobileScreen = useMediaQuery(phoneOnly);
  const { isCountySearch } = useContext(CountySearchContext);
  const { clearSearchFilters } = useContext(SearchFilterContext);

  const coverageTypeFromStore = useCoverageType();
  const searchParamsObject = Object.fromEntries(searchParams.entries());
  const {
    searchType = '',
    search = '',
    coverageType: coverageTypeFromParams,
    showAllVirtualCareList,
  } = searchParamsObject;
  const coverageType = coverageTypeFromParams || coverageTypeFromStore;

  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
  const uiMessaging = useLagoon('ui-messaging')();
  const typeaheadPreferenceCodes = useLagoon('typeahead-preference')();
  const suppressFacilityFlags = getSuppressFacilityFlags();
  const suppressPremiumCare = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );
  const [
    eapCodeFlag,
    showTierProviderTag,
    enableNavigateToCorrectCategory,
    enableSkipMixedSearchResult,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.EAP_CODE,
    FeatureFlags.FEATURE_FLAG_KEY_CHIP_MAP[
      Constants.CHIPS_CATEGORIES.PRIMARY_CARE
    ][SECTION_TERM.TIER_ONE],
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NAVIGATE_TO_CORRECT_CATEGORY,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SKIP_MIXED_RESULT_PAGE,
  ]);
  const isVirtualCareSuppressedForEmpire = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_EMPIRE
  );
  const { translatedPsxKeyword, heading = '' } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      heading: state.typeaheadSearchStore.heading,
      translatedPsxKeyword: state.typeaheadSearchStore.translatedPsxKeyword,
      typeaheadSearchStore: state.typeaheadSearchStore,
    }))
  );

  const memberHasRPK = useStore(StoreKeys.TIER1_PLAN_FLAG);
  const [breadcrumbUrls, setBreadcrumbUrls] =
    useSessionStorage<BreadcrumbSessionStorage>(
      Constants.STORAGE_KEYS.SESSION.BREADCRUMB_URLS,
      {}
    );

  const isSmartChoiceEnabled = useSmartChoice();

  const isSpecialtySearch = searchType === Constants.SEARCH_TYPES.SPECIALTY;

  const isSearchModified = useRef(false);
  const [isPageTracked, setPageTracked] = useState<boolean>(false);

  const { dependentSeqNbr, choosePCP } = usePCPStore(
    useShallow((state: any) => ({
      choosePCP: state.pcpSearchState.choosePCP,
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
    }))
  );

  const { originLinkNameForAnalytics = '', setAnalyticsState } =
    useAnalyticsStore(
      useShallow((state: any) => ({
        originLinkNameForAnalytics:
          state.analyticsState.originLinkNameForAnalytics,
        setAnalyticsState: state.setAnalyticsState,
      }))
    );

  const { setErrorStore } = useErrorStore(
    useShallow((state: any) => ({
      setErrorStore: state.setErrorStore,
    }))
  );

  const { chipValue, setChipValue, setCoverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      chipValue: state.chipValue,
      setChipValue: state.setChipValue,
      setCoverageType: state.setCoverageType,
    }))
  );

  const OHCNSpolicyIds = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.STATE_SPECIFIC_DISCLAIMER,
  })?.policyIDs;
  const isOHCNS = isOHCNSMember(OHCNSpolicyIds);

  const currentMember = useCurrentMemberOrDependentInfo(dependentSeqNbr);

  const ddpCodeUser = getLoggedInMember()?.ddpCode;

  const {
    isLoading,
    search: searchTitle,
    uniqueSearch,
    virtualCare,
    includeSpecialityRollupCodes,
    acceptingNewPatients,
    specialtyCodeFromQuery,
    preSelectEapFilter,
    userZip,
    specialtyCode,
    linkName,
    combinedIncludeSpecialityRollupCodes,
    searchMethod,
    searchTerm,
    medicalSpeciality,
    isMixedSuggestions,
    preferredFacilities,
    parallelCategoryCountResults,
    isEmptyResults,
    categoryCountResults,
    genderCode,
    virtualCareCode,
    essentialCommunityProviderCode,
    invokeLlmModel,
    combinedSearchResults,
    combinedSearchResultsError,
    providerIsLoading,
    providersResult,
    medGrpResult,
    organizationResult,
    orgIsLoading,
    medGrpIsLoading,
    providerHasError,
    showFacilities,
    showProviderGroups,
    showProviders,
    areaOfExpertise,
    isCustomEmptyResults,
    showVirtualResultsOnly,
  } = useTypeAheadData({});

  const hasOnlyPseudoRollupCodes = useCheckForPseudoRollupCodes(
    includeSpecialityRollupCodes
  );
  const [searchButtonResults] = useSessionStorage<any>(
    Constants.STORAGE_KEYS.SESSION.TYPE_AHEAD_SEARCH_RESULTS,
    []
  );
  const isPreEffective = useCurrentMemberIsPreEffective();
  const virtualVisitChipEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_VISIT
  );
  const showVirtualCare = getFeatureFlag(
    featureFlags,
    FeatureFlags.FEATURE_FLAG_KEY_CHIP_MAP[Constants.CHIPS_CATEGORIES.ALL][
      SECTION_TERM.VIRTUAL_CARE
    ]
  );
  const [enableCategoryCountV2] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CATEGORY_COUNT_V2,
  ]);
  const suppressVirtualCareQuickCare = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE
  );
  const changeRCLogicForVC: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.CHANGE_ROLLUP_CODE_LOGIC_VC
  );

  const includeSpecialityRollupCodesArray =
    includeSpecialityRollupCodes?.split(',');
  const { planToken } = getPlanSelectorVariables();
  const allConfigs = useLagoon('config');
  const hbpbCodes =
    getLagoonConfigValue(allConfigs, 'VISION_NON_UHC_ICARE')?.split(',') || [];
  const vcRollupCodesV2 = getLagoonConfigValue(
    allConfigs,
    'VIRTUAL_CARE_ROLLUP_CODES_V2'
  )
    ?.split(',')
    .map((v) => v.trim());

  const vcRollupCodes = getLagoonConfigValue(
    allConfigs,
    'VIRTUAL_CARE_ROLLUP_CODES'
  )
    ?.split(',')
    .map((v) => v.trim());
  const isNonUHCVisionSelected =
    isMNRHbpbCodePresent(currentMember, hbpbCodes) &&
    searchButtonResults
      ?.find((item) => item?.suggestion === searchTitle)
      ?.coverageType?.includes('V');

  function hasAllAllowedRollupCodesVC(): boolean {
    if (changeRCLogicForVC) {
      return includeSpecialityRollupCodesArray.some((vcr) =>
        vcRollupCodesV2.includes(vcr?.trim())
      );
    }
    return includeSpecialityRollupCodesArray.every((vcr) =>
      vcRollupCodes.includes(vcr?.trim())
    );
  }
  const vcEnabledChips = getLagoonConfigValue(
    allConfigs,
    'VIRTUAL_CARE_ENABLED_CHIPS'
  )
    ?.split(',')
    .map((v) => v.trim());
  const enableVirtualVisitSection =
    virtualVisitChipEnabled &&
    virtualCareCardShow &&
    showVirtualCare &&
    !isPreEffective &&
    !suppressVirtualCareQuickCare;

  const isVirtualVisitsShowing =
    enableVirtualVisitSection &&
    vcEnabledChips.includes(chipValue) &&
    hasAllAllowedRollupCodesVC();

  useEffect(() => {
    clearSearchFilters();
  }, []);

  useEffect(() => {
    if (choosePCP) {
      adobePageTrackEvent();
      setPageTracked(true);
    }
  }, [choosePCP]);

  useEffect(() => {
    setPageTracked(false);
  }, [chipValue]);

  const allProviders = getAllProvidersData(combinedSearchResults, chipValue);
  const combineProviders = combineProvidersAcrossSections(
    combinedSearchResults,
    false
  );

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: 'overview',
    sitesectionLevel1: 'search results',
    sitesectionLevel2: 'category',
    providerBlock: {
      type: convertCoverageType(coverageType),
    },
    impressionBlock: {
      type: 'provider',
      indicator:
        combineProviders?.length > 0
          ? getIndicatorsForImpressions(
              combineProviders,
              featureFlags,
              false,
              {
                suppressPremiumCare,
                ddpCodeUser,
                suppressFacilityFlags,
                memberHasRPK,
                isCountySearch,
              },
              currentMember
            )
          : '',
      ...(isSmartChoiceEnabled && {
        message: 'smart choice displayed',
      }),
      providerTier: getTier1Indicator(showTierProviderTag, allProviders),
    },
  });

  const [isAllResultsLoading, setIsAllResultsLoading] =
    useState<boolean>(false);

  const crisisKeywords = useLagoon(Constants.LAGOON_TABLE.CRISIS_KEYWORDS)();

  const [isCrisisModalOpen, setIsCrisisModalOpen] = React.useState(false);
  const [hasCrisisModalBeenShown, setHasCrisisModalBeenShown] =
    useSessionStorage<boolean>(
      Constants.STORAGE_KEYS.SESSION.CRISIS_MODAL_DISMISSED,
      false
    );
  const crisisModalRef = React.useRef(null);

  const crisisKeywordList = crisisKeywords
    ?.filter((item) => item?.keyword)
    ?.map((item) => item.keyword.toLowerCase());

  const [enableCrisisKeywords] = useFeatureFlag([
    FeatureFlags.ENABLE_CRISIS_KEYWORDS,
  ]);

  useEffect(() => {
    setIsAllResultsLoading(
      providerIsLoading || orgIsLoading || medGrpIsLoading
    );
  }, [providerIsLoading, orgIsLoading, medGrpIsLoading, isSpecialtySearch]);

  useEffect(() => {
    const {
      totalCountPractitioners,
      totalCountOrganizations,
      totalCountMedicalGroup,
    } = combinedSearchResults || {};
    const isResultsLoaded =
      (!showProviders || totalCountPractitioners !== undefined) &&
      (!showFacilities || totalCountOrganizations !== undefined) &&
      (!showProviderGroups || totalCountMedicalGroup !== undefined);

    const totalNumberOfResults = getNoOfResultsForAnalytics(
      combinedSearchResults,
      { showProviders, showFacilities, showProviderGroups }
    );
    if (
      isResultsLoaded &&
      !isLoading &&
      chipValue &&
      !choosePCP &&
      !isAllResultsLoading &&
      totalNumberOfResults > 0
    ) {
      const adobeSearchMethod = compareAndReturn(searchMethod, 'guided');
      const adobeSearchTerm =
        adobeSearchMethod === 'guided'
          ? ''
          : compareAndReturn(searchTerm, search);
      const adobeEventBody = {
        term: adobeSearchTerm,
        type: getProviderType(providerType),
        linkName: originLinkNameForAnalytics,
        method: adobeSearchMethod,
        filters: chipValue?.toLowerCase(),
        numberOfResults: totalNumberOfResults,
        customAttributesBlock: {
          providerType: getProviderTypesFromDisplayedResults(
            combinedSearchResults
          ),
          searchLocation: getSearchLocation(storedLocation),
        },
      };

      adobePageTrackEvent();
      setPageTracked(true);

      if (!isSearchModified.current) {
        adobeStandardSearchEvent(adobeEventBody);
        isSearchModified.current = true;
      } else {
        adobeSearchModifiedEvent(adobeEventBody);
      }

      if (isMixedSuggestions && isSpecialtySearch) {
        adobeImpressionTrackEvent({
          type: 'search modify',
          message:
            'search instead for providers that include typed term in their name',
          searchTerm: adobeSearchTerm,
          method: adobeSearchMethod,
        });
      }
      if (isMixedSuggestions && !isSpecialtySearch) {
        adobeImpressionTrackEvent({
          type: 'search modify',
          message: 'search instead for providers that can treat typed term',
          searchTerm: adobeSearchTerm,
          method: adobeSearchMethod,
        });
      }
    } else if (
      isNonUHCVisionSelected &&
      !isLoading &&
      chipValue &&
      !choosePCP &&
      !isAllResultsLoading
    ) {
      adobePageTrackEvent();
      setPageTracked(true);
    }
    const sectionType = getSectionType(chipValue);
    const specialityResults = categoryCountResults?.specialityResults;

    const urlParams = convertObjectToUrlParams({} as ChipState, {
      ...searchParamsObject,
      chipValue: chipValue || searchParamsObject.chipValue,
      coverageType: coverageType || searchParamsObject.coverageType,
      sectionType,
      [Constants.PRE_SELECT_FILTERS]: true,
      ...(choosePCP && { providerType }),
    });
    const filteredValues = specialityResults?.filter(
      (item: any) => item.chipEnabled === true
    );

    const chipCount = filteredValues?.length || 0;
    if (
      !currentMember?.isIFPEarlyPay &&
      chipCount == 1 &&
      !isVirtualVisitsShowing
    ) {
      const providertypes =
        enableCategoryCountV2 === true
          ? filteredValues[0]?.providerTypeList
          : filteredValues[0]?.providerTypes;

      if (providertypes?.length === 1 && enableSkipMixedSearchResult) {
        setBreadcrumbUrls({
          [ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.key]:
            ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.path +
            window.location.search,
        });
        navigate(
          `${ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.path}/${urlParams}`
        );
      }
    }
  }, [isAllResultsLoading, isSpecialtySearch, chipValue]);

  const uesVirtualCareKeywordMappingTable = useLagoon(
    'ues-virtual-care-keyword-mapping'
  )();
  const programPromotionsRollupCodesTable = useLagoon(
    'program-promotions-pseudo-rollupcode-mapping'
  )();
  const filterVcProvidersForPrimaryCareChip = getLagoonConfigValue(
    allConfigs,
    'FILTER_VIRTUAL_CARE_PROVIDER_FOR_PRIMARY_CHIP'
  );
  const enablePseudoRollupCodeOnlyMatch = getLagoonConfigValue(
    allConfigs,
    'ENABLE_PSEUDO_ROLLUP_CODE_ONLY_MATCH'
  );
  const eligibilities = useVirtualCareStore((state) => state.eligibilities);
  const { setVirtualCareProviders } = useVirtualCareStore((state) => state);
  const ceOffersData = useCeOffersStore((state) => state.ceOffersData);
  const setProgramPromotionsForResultPage = useCeOffersStore(
    (state) => state.setProgramPromotionsForResultPage
  );

  const virtualCareProviders = useMemo(
    () =>
      getCategoryVirtualCareProvidersFromEligibilities({
        search: searchTitle,
        includeSpecialityRollupCodes,
        uesVirtualCareKeywordMappingTable,
        eligibilities: eligibilities[planToken] || [],
        enablePseudoRollupCodeOnlyMatch,
        filterVcProvidersForPrimaryCareChip: !showAllVirtualCareList
          ? filterVcProvidersForPrimaryCareChip
          : '',
        chipValue,
        showAllVirtualCareList: !!showAllVirtualCareList,
      }),
    [chipValue, searchTitle, eligibilities]
  );

  const mpCodes = useMemo(
    () =>
      getMpCodes(
        includeSpecialityRollupCodes,
        programPromotionsRollupCodesTable
      ),
    [includeSpecialityRollupCodes, programPromotionsRollupCodesTable]
  );

  const filteredPromotions = useMemo(() => {
    if (ceOffersData?.length > 0 && mpCodes?.length > 0) {
      return getProgramPromotionsForTypeahead(ceOffersData, mpCodes);
    }
    return [];
  }, [ceOffersData, mpCodes]);

  useEffect(() => {
    setProgramPromotionsForResultPage(filteredPromotions);
  }, [filteredPromotions]);

  useEffect(() => {
    setVirtualCareProviders(virtualCareProviders);
  }, [virtualCareProviders]);

  const normalizedSearchTitle = searchTitle?.toLowerCase() || '';
  const shouldShowCrisisModalLogic = (
    isLoading: boolean,
    chipValue: any,
    choosePCP: boolean,
    isAllResultsLoading: boolean,
    enableCrisisKeywords: boolean,
    crisisKeywordList: string[] | undefined,
    normalizedSearchTitle: string,
    hasCrisisModalBeenShown: boolean,
    isPageTracked: boolean
  ): boolean =>
    !isLoading &&
    chipValue &&
    !choosePCP &&
    !isAllResultsLoading &&
    enableCrisisKeywords &&
    crisisKeywordList?.includes(normalizedSearchTitle) &&
    !hasCrisisModalBeenShown &&
    !isPageTracked;

  useEffect(() => {
    if (
      shouldShowCrisisModalLogic(
        isLoading,
        chipValue,
        choosePCP,
        isAllResultsLoading,
        enableCrisisKeywords,
        crisisKeywordList,
        normalizedSearchTitle,
        hasCrisisModalBeenShown,
        isPageTracked
      )
    ) {
      adobeModalTrackEvent('search crisis intervention modal');
      setIsCrisisModalOpen(true);
    }
  }, [
    isLoading,
    chipValue,
    choosePCP,
    isAllResultsLoading,
    enableCrisisKeywords,
    crisisKeywordList,
    normalizedSearchTitle,
    hasCrisisModalBeenShown,
    isPageTracked,
  ]);

  // Function to handle modal close
  const handleCrisisModalClose = () => {
    setIsCrisisModalOpen(false);
    setHasCrisisModalBeenShown(true);
  };
  const getSectionType = (chipValue: string) => {
    if (
      chipValue !== CareCategories.FACILITIES &&
      chipValue !== CareCategories.HEALTH_SUPPLIES &&
      showProviders
    ) {
      return SectionType.PROVIDER;
    }
    if (
      chipValue !== CareCategories.FACILITIES &&
      chipValue !== CareCategories.HEALTH_SUPPLIES &&
      showProviderGroups
    ) {
      return SectionType.PROVIDER_GROUP;
    }
    if (!choosePCP && showFacilities) {
      return SectionType.FACILITY;
    }
    return null;
  };

  const noResultUrlParams = convertObjectToUrlParams({} as ChipState, {
    chipValue,
    includeSpecialityRollupCodes,
    linkName,
    medicalSpeciality,
    originLinkNameForAnalytics,
    search,
    searchType,
    specialtyCode,
  });

  const showMedicalSpecialityNSRPage =
    medicalSpeciality &&
    chipValue === Constants.CHIPS_CATEGORIES.MEDICAL_SPECIALISTS;

  useEffect(() => {
    if (!isLoading && isEmptyResults && showMedicalSpecialityNSRPage) {
      navigate(
        `${ConstantsRoutes.NULL_SPECIALTY_RESULTS.path}/${noResultUrlParams}`
      );
    }
    if (combinedSearchResultsError) {
      const errorStoreValues = {
        uiSection: linkName || originLinkNameForAnalytics,
      };
      setErrorStore(errorStoreValues);
      const urlParams = convertObjectToUrlParams({} as ChipState, {
        ...errorStoreValues,
      });
      navigate(
        `${
          errorCodeRouteMap[providerHasError?.status] ||
          errorCodeRouteMap['4xx']
        }${urlParams}`
      );
    }
  }, [
    isLoading,
    combinedSearchResultsError,
    showMedicalSpecialityNSRPage,
    noResultUrlParams,
  ]);
  useEffect(() => {
    if (chipValue !== CareCategories.ALL) {
      document.title = chipValue
        ? `${t(`${chipValue} - ${t('CHOOSE_PCP_FLOW.CHOOSE_PCP')}`)} | ${
            Constants.SITE_NAME
          }`
        : Constants.SITE_NAME;
    }
  }, [chipValue, searchTitle]);
  const activeMemberLOB = memberProfile?.[0]?.lineOfBusiness;

  useEffect(() => {
    if (activeMemberLOB === LOB.MNR || activeMemberLOB === LOB.CNS) {
      storage.session.set(ACTIVE_MEMBER_INDEX, '0');
    }
    let showVirtualCareCard: boolean = false;
    if (memberProfile[0]?.eligibility[0]?.memberHealthCoverage?.coverageType) {
      const coverageType =
        memberProfile[0]?.eligibility[0]?.memberHealthCoverage.coverageType.find(
          (x: CoverageType) =>
            x.typeCode &&
            x.typeCode.desc === Constants.COVERAGE_TYPECODE_TO_CHIP.M
        );

      if (coverageType && coverageType?.designatedNetwork) {
        showVirtualCareCard = getMemberHasDesignatedNetwork(
          coverageType,
          'DVCN'
        );
      }
    }

    showVirtualCareCard = memberProfile[0]?.isCnSMember
      ? !!(
          !memberProfile[0].isCnSDsnpMember &&
          memberProfile[0].isTelemedicineEligible
        )
      : showVirtualCareCard && !isVirtualCareSuppressedForEmpire;

    setVirtualCareCardShow(showVirtualCareCard);
  }, [
    memberProfile,
    activeMemberIndex,
    isVirtualCareSuppressedForEmpire,
    activeMemberLOB,
  ]);

  useEffect(() => {
    if (currentMember?.isIFPEarlyPay) {
      return;
    }

    const { pathname, search } = location;

    breadcrumbUrls[
      ConstantsRoutes.PROVIDER_SEARCH_RESULTS.key
    ] = `${pathname}${search}`;
    const updatedBreadCrumbUrls = pick(
      breadcrumbUrls,
      ConstantsRoutes.PROVIDER_SEARCH_RESULTS.key
    );
    setBreadcrumbUrls(updatedBreadCrumbUrls);
  }, [searchParams]);

  const { specialityResults, nameResults } = categoryCountResults;
  const enabledChipForNameSearch = getFirstEnabledChip(nameResults);
  const enabledChipForSpecialitySearch = getFirstEnabledChip(specialityResults);

  const onSpecialtyResultClicked = (
    isSpecialtySearch: boolean,
    title: string = ''
  ) => {
    const { specialityResults } = categoryCountResults;
    const navigateToChipCategory = enableNavigateToCorrectCategory
      ? getCorrectChipCategory(
          combinedIncludeSpecialityRollupCodes,
          typeaheadPreferenceCodes,
          specialityResults
        )
      : '';
    setIsAllResultsLoading(true);
    const firstEnabledChip = isSpecialtySearch
      ? enabledChipForSpecialitySearch
      : enabledChipForNameSearch;
    const chipToSelect =
      isSpecialtySearch && navigateToChipCategory
        ? navigateToChipCategory
        : firstEnabledChip;

    const selectedCoverageType = getSelectedCoverageType(chipToSelect);
    setCoverageType(ReverseCoverageTypesCodes[selectedCoverageType]);
    setChipValue(chipToSelect);
    setSearchParams(
      (searchParams) => {
        searchParams.set('chipValue', chipToSelect);
        searchParams.set(
          'coverageType',
          ReverseCoverageTypesCodes[selectedCoverageType]
        );
        searchParams.set('searchType', getSearchType(isSpecialtySearch));
        return searchParams;
      },
      { replace: true }
    );

    adobeLinkTrackEvent({
      name: title,
      location: 'body:result heading',
    });

    setAnalyticsState({ originLinkNameForAnalytics: title });
  };

  const getChipsToShowOnHeader = () => {
    if (enabledChipForNameSearch && !isSpecialtySearch) {
      return nameResults || [];
    }
    return specialityResults || [];
  };

  const breadcrumbs = [
    {
      title: t('BC Results'),
      href: '',
    },
  ];

  const showLoadingSkeleton =
    isLoading ||
    (!choosePCP && !isEmptyResults && chipValue === CareCategories.ALL);

  const { isResultsLoaded, hasResults } = resolvedCombinedSearchResults({
    combinedSearchResults,
    uniqueSearch,
    showProviders,
    showFacilities,
    showProviderGroups,
  });

  let showNullSearchResultsSection;
  if (
    isMNRHbpbCodePresent(currentMember, hbpbCodes) &&
    searchButtonResults
      ?.find((item) => item?.suggestion === searchTitle)
      ?.coverageType?.includes('V')
  ) {
    showNullSearchResultsSection = false;
  } else {
    showNullSearchResultsSection =
      (isEmptyResults || (isResultsLoaded && !hasResults)) &&
      !showMedicalSpecialityNSRPage &&
      !showVirtualResultsOnly;
  }

  return (
    <React.Fragment>
      {choosePCP ? (
        <PSXHeader
          breadcrumbs={breadcrumbs}
          dataTestId="search-results-search-form"
          showChips={false}
          showChoosePCPHeader
          showMemberSelection={false}
        />
      ) : (
        <PSXHeader
          breadcrumbs={breadcrumbs}
          categoryCount={getChipsToShowOnHeader()}
          dataTestId="search-results-search-form"
          isMixedResultsPage
          showChips={!isEmptyResults}
          showChoosePCPHeader={false}
          showMemberSelection={!mobileScreen}
          showSearchInputBackButton={mobileScreen}
        />
      )}
      {isCustomEmptyResults ? (
        <CustomNullSearchPage onCommonSearchChipClicked={() => {}} />
      ) : null}
      {!isCustomEmptyResults && showAllVirtualCareList === 'true' && (
        <VirtualCareList
          heading={heading}
          isLoading={false}
          mobileScreen={mobileScreen}
          searchTerm={searchTerm}
          serviceType={virtualCareProviders?.[0]?.serviceType}
          setIsVirtualCareListView={() => {}}
          virtualCareProviders={virtualCareProviders}
        />
      )}
      {!isCustomEmptyResults && showAllVirtualCareList !== 'true' && (
        <>
          {showLoadingSkeleton ? (
            <SectionSkeleton />
          ) : showNullSearchResultsSection ? (
            <NullResultsPage
              emptyResults={isEmptyResults}
              onCommonSearchChipClicked={() => {}}
              searchTitle={searchTitle}
              specialtyCode={specialtyCode}
              specialtySearch={isSpecialtySearch}
            />
          ) : (
            <SearchedResultSectionData
              acceptingNewPatients={acceptingNewPatients}
              areaOfExpertise={areaOfExpertise}
              categoryCount={getChipsToShowOnHeader()}
              choosePCP={choosePCP || false}
              contextValue={chipValue}
              data={combinedSearchResults}
              dependentSeqNbr={dependentSeqNbr || ''}
              eapCodeFlag={eapCodeFlag}
              essentialCommunityProviderCode={essentialCommunityProviderCode}
              facilitiesHeaders={organizationResult.headers}
              genderCode={genderCode}
              hasOnlyPseudoRollupCodes={hasOnlyPseudoRollupCodes}
              includeSpecialityRollupCodes={includeSpecialityRollupCodes}
              invokeLlmModel={invokeLlmModel}
              isMixedResultsPage
              isMixedSuggestions={isMixedSuggestions}
              isNameSearchEnabled={enabledChipForNameSearch !== ''}
              isPageTracked={isPageTracked}
              isSpecialtySearchEnabled={
                hasOnlyPseudoRollupCodes ||
                enabledChipForSpecialitySearch !== ''
              }
              medGrpIsLoading={medGrpIsLoading}
              medicalGroupHeaders={medGrpResult.headers}
              onSpecialtyResultClicked={onSpecialtyResultClicked}
              orgIsLoading={orgIsLoading}
              preSelectEapFilter={preSelectEapFilter}
              preferredFacility={preferredFacilities}
              providerHeaders={providersResult.headers}
              providerIsLoading={providerIsLoading}
              resultType={chipValue}
              searchMethod={searchMethod}
              searchTerm={searchTerm}
              searchTitle={searchTitle}
              searchType={getSearchType(isSpecialtySearch)}
              showChips={!isEmptyResults || isNonUHCVisionSelected}
              showFacilities={showFacilities}
              showProviderGroups={showProviderGroups}
              showProviders={showProviders}
              specialtyCode={specialtyCode}
              specialtyCodeFromQuery={specialtyCodeFromQuery}
              specialtyResults={parallelCategoryCountResults?.specialityResults}
              specialtySearch={isSpecialtySearch}
              translatedPsxKeyword={translatedPsxKeyword}
              userZip={userZip}
              virtualCare={virtualCare}
              virtualCareCardShowFlag={virtualCareCardShow}
              virtualCareCode={virtualCareCode}
            />
          )}
        </>
      )}
      <CrisisInterventionModal
        isOpen={isCrisisModalOpen}
        ref={crisisModalRef}
        setIsOpen={handleCrisisModalClose}
      />
      <NullPageContainerStyled hidden={!isEmptyResults}>
        <StillNeedHelp />
        {isOHCNS && <DisclaimersContainer />}
      </NullPageContainerStyled>
    </React.Fragment>
  );
};
